/**
 * My stuff hub — route canon (Phase 1).
 * Path builders only. Segment panels own their data.
 */

export const MY_STUFF_SEGMENTS = ["selling", "buying", "looking"] as const;

export type MyStuffSegment = (typeof MY_STUFF_SEGMENTS)[number];

export const MY_STUFF_SEGMENT_LABEL: Record<MyStuffSegment, string> = {
  selling: "Selling",
  buying: "Buying",
  looking: "Looking for",
};

/** Default segment when opening `/my-stuff` (D3). */
export const MY_STUFF_DEFAULT_SEGMENT: MyStuffSegment = "selling";

const LEGACY_LIST_TO_SEGMENT: Record<string, MyStuffSegment> = {
  "/my-items": "selling",
  "/my-purchases": "buying",
  "/my-requests": "looking",
};

export function isMyStuffSegment(value: string): value is MyStuffSegment {
  return (MY_STUFF_SEGMENTS as readonly string[]).includes(value);
}

/** Canonical hub path for a segment. */
export function myStuffPath(
  segment: MyStuffSegment = MY_STUFF_DEFAULT_SEGMENT,
): string {
  return `/my-stuff/${segment}`;
}

/**
 * Map a legacy list path to the hub segment path, preserving the query string.
 * Purchase detail (`/my-purchases/:id`) is not a list route and returns null.
 */
export function legacyListPathToMyStuff(
  pathname: string,
  search = "",
): string | null {
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const segment = LEGACY_LIST_TO_SEGMENT[path];
  if (!segment) return null;
  const query = search.startsWith("?") ? search : search ? `?${search}` : "";
  return `${myStuffPath(segment)}${query}`;
}

/** Serialize Next.js `searchParams` for `legacyListPathToMyStuff`. */
export function searchParamsRecordToQuery(
  searchParams: Record<string, string | string[] | undefined>,
): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(searchParams)) {
    if (typeof value === "string") params.append(key, value);
    else if (Array.isArray(value)) {
      for (const entry of value) params.append(key, entry);
    }
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function myStuffSegmentFromPathname(
  pathname: string,
): MyStuffSegment | null {
  const path =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const match = path.match(/^\/my-stuff\/(selling|buying|looking)$/);
  if (!match) return null;
  return match[1] as MyStuffSegment;
}
